import { AssigneePicker, AssigneeValue } from '@Purchasing/ui/AssigneePicker'
import { useSystemUsers } from '@Purchasing/Hooks/useSystemUsers/useSystemUsers'
import { Container, ContainerHeader } from '@Core/Components'
import { Accent, PageHeading } from '@/components/brand/brand'
import { Button as UiButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { FileText, Hash, Layers, PenLine, ReceiptText, Search } from 'lucide-react'
import { BackButton } from '@Core/Components/BackButton/BackButton'
import { Loader } from '@Core/Components/Loader/Loader'
import { PurchaseProcess } from '@Purchasing/Domain/Model/PurchaseProcess'
import { BatchCreationResult } from '@Purchasing/Data/DataSource/API/Entity/PurchaseProcess'
import { BudgetItemOrganizer } from '@Purchasing/NewPurchaseProcess/BudgetItemOrganizer'
import { useCreateProcessesBatch } from '@Purchasing/Hooks/useCreateProcessesBatch/useCreateProcessesBatch'
import { ManualProcessForm } from '@Purchasing/NewPurchaseProcess/ManualProcessForm'
import { usePurchasePreparation } from '@Purchasing/Hooks/usePurchasePreparation/usePurchasePreparation'
import { RoutesPaths } from '@Purchasing/RoutesPaths'
import { roles } from '@Purchasing/roles'
import { formatRouteId } from '@Utils/Formatters/formatRouteId'
import { PermissionWrapper } from '@Core/Components/PermissionWrapper/PermissionWrapper'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FormEvent, ReactNode, useEffect, useState } from 'react'

const singleSchema = z.object({
    title: z.string().min(3, 'Informe um título com pelo menos 3 caracteres'),
    quantity: z
        .number({ invalid_type_error: 'Informe a quantidade' })
        .int('Quantidade deve ser um número inteiro')
        .positive('Quantidade deve ser maior que zero'),
    notes: z.string().optional(),
})

type SingleValues = z.infer<typeof singleSchema>
type Mode = 'split' | 'single'
type Origin = 'budget' | 'manual'

const fieldLabel = 'mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground'

const Step = ({ n, title, children }: { n: number; title: string; children: ReactNode }) => (
    <section className="rounded-card border border-border p-4 md:p-5">
        <h2 className="mb-3 flex items-center gap-2 font-display text-base font-bold">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">{n}</span>
            {title}
        </h2>
        {children}
    </section>
)

export const NewPurchaseProcess = () => {
    const navigate = useNavigate()
    const [origin, setOrigin] = useState<Origin>('budget')
    // Responsável dos processos criados: começa com quem está criando.
    const { users, currentUserId } = useSystemUsers()
    const [assignee, setAssignee] = useState<AssigneeValue | null | undefined>(undefined)
    useEffect(() => {
        if (assignee !== undefined || !currentUserId || !users.length) return
        const me = users.find((user) => user.id === currentUserId)
        setAssignee(me ? { uid: me.id, name: me.name } : null)
    }, [assignee, currentUserId, users])
    const chooseAssignee = (id: string | null) => {
        const user = users.find((u) => u.id === id)
        setAssignee(user ? { uid: user.id, name: user.name } : null)
    }
    const [mode, setMode] = useState<Mode>('split')
    const [budgetCodeInput, setBudgetCodeInput] = useState('')
    const [searchedCode, setSearchedCode] = useState<number | null>(null)
    const [searchError, setSearchError] = useState<string | null>(null)
    const { preparation, loading: loadingPreparation, error: preparationError, load } = usePurchasePreparation()
    const { createBatch, loading: creatingBatch } = useCreateProcessesBatch()
    const [batchResult, setBatchResult] = useState<BatchCreationResult | null>(null)

    const context = preparation?.sourceContext
    const itemCount = context?.items?.length ?? 0
    const totalQuantity = (context?.items ?? []).reduce((sum, item) => sum + (item.quantity ?? 0), 0)
    // Itens já vinculados a outro processo não podem entrar de novo (§25/§26).
    const boundIds = new Set((preparation?.existingProcessMappings ?? []).map((m) => m.sourceItemExternalId))
    const freeItems = (context?.items ?? []).filter((item) => item.externalId && !boundIds.has(item.externalId))

    const { register, handleSubmit, reset, formState: { errors } } = useForm<SingleValues>({
        resolver: zodResolver(singleSchema),
        defaultValues: { quantity: 1 },
    })

    const handleSearchBudget = async (event?: FormEvent) => {
        event?.preventDefault()
        setSearchError(null)
        setBatchResult(null)
        const code = Number(budgetCodeInput)
        if (!code || Number.isNaN(code)) {
            setSearchError('Informe o código do orçamento para buscar')
            return
        }
        const result = await load(code)
        if (result) {
            setSearchedCode(code)
            // Sugestão para o processo único: nome do primeiro item e soma das quantidades.
            const items = result.sourceContext?.items ?? []
            reset({
                title: items.length === 1 ? items[0]?.name ?? '' : `Orçamento #${code}`,
                quantity: items.reduce((sum, item) => sum + (item.quantity ?? 0), 0) || 1,
                notes: '',
            })
        } else {
            setSearchedCode(null)
        }
    }

    const handleConfirmOrganizer = async (
        groups: { temporaryId: string; title: string; quantity: number | null; items: { sourceItemExternalId: string }[] }[]
    ) => {
        if (!searchedCode) return
        const result = await createBatch({
            budgetCode: searchedCode,
            assignedTo: assignee?.uid,
            processes: groups.map((g) => ({
                title: g.title,
                quantity: g.quantity ?? 0,
                sourceItemExternalIds: g.items.map((i) => i.sourceItemExternalId),
            })),
        })
        if (result) setBatchResult(result)
    }

    const onSubmitSingle = handleSubmit(async (values) => {
        if (!searchedCode) return
        // Processo único = um grupo com todos os itens livres do orçamento.
        const result = await createBatch({
            budgetCode: searchedCode,
            assignedTo: assignee?.uid,
            processes: [{
                title: values.title,
                quantity: values.quantity,
                notes: values.notes || undefined,
                sourceItemExternalIds: freeItems.map((item) => String(item.externalId)),
            }],
        })
        if (result) setBatchResult(result)
    })

    if (batchResult) {
        return (
            <Container>
                <BackButton />
                <section className="mx-auto mt-6 max-w-xl rounded-card border border-border p-5" data-testid="batch-success">
                    <h2 className="mb-3 font-display text-lg font-bold" data-testid="batch-success-heading">
                        {batchResult.created.length}{' '}
                        {batchResult.created.length === 1 ? 'processo criado com sucesso' : 'processos criados com sucesso'}
                    </h2>
                    <ul className="divide-y divide-border">
                        {batchResult.created.map((c) => (
                            <li key={c.id} className="flex items-center justify-between gap-3 py-2">
                                <div className="min-w-0">
                                    <p className="text-sm font-semibold">{c.code}</p>
                                    <p className="truncate text-xs text-muted-foreground">{c.title}</p>
                                </div>
                                <UiButton type="button" size="sm" variant="outline" onClick={() => navigate(formatRouteId(RoutesPaths.PurchasingDetail, c.id))}>
                                    Ver processo
                                </UiButton>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <UiButton type="button" onClick={() => navigate(RoutesPaths.PurchasingList)}>Ver todos os processos</UiButton>
                        <UiButton type="button" variant="ghost" onClick={() => { setBatchResult(null); setBudgetCodeInput(''); setSearchedCode(null) }}>
                            Criar outros
                        </UiButton>
                    </div>
                </section>
            </Container>
        )
    }

    return (
        <Container>
            <BackButton />
            <ContainerHeader>
                <PageHeading eyebrow="Compras · novo processo">
                    {origin === 'manual' ? <>Processo <Accent>manual</Accent></> : <>Do <Accent>orçamento</Accent> para a compra</>}
                </PageHeading>
            </ContainerHeader>

            <div className="mb-4 inline-flex flex-wrap rounded-full bg-secondary p-1" role="tablist" aria-label="Origem do processo">
                {([
                    ['budget', 'Do orçamento', ReceiptText, 'Busca os itens de um orçamento do Brinde.me'],
                    ['manual', 'Manual', PenLine, 'Item fora do Brinde.me, tabela de preço ou estoque'],
                ] as const).map(([value, label, Icon, hint]) => (
                    <button
                        key={value}
                        type="button"
                        role="tab"
                        title={hint}
                        aria-selected={origin === value}
                        onClick={() => setOrigin(value)}
                        className={cn(
                            'inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
                            origin === value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-brand-deep'
                        )}
                    >
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                        {label}
                    </button>
                ))}
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">Responsável</span>
                <AssigneePicker value={assignee} onChange={chooseAssignee} />
                <span className="text-xs text-muted-foreground">Vale para todos os processos criados agora.</span>
            </div>

            {origin === 'manual' ? <ManualProcessForm assignedTo={assignee?.uid} /> : (
            <div className="flex flex-col gap-4">
                <Step n={1} title="Qual orçamento?">
                    <form className="flex flex-wrap items-end gap-2" onSubmit={handleSearchBudget}>
                        <label className="block">
                            <span className={fieldLabel}>Código do orçamento</span>
                            <div className="relative">
                                <Hash className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                                <Input
                                    aria-label="Código do orçamento"
                                    inputMode="numeric"
                                    placeholder="Ex.: 1802"
                                    className="h-10 w-48 pl-9"
                                    value={budgetCodeInput}
                                    onChange={(e) => setBudgetCodeInput(e.target.value.replace(/\D/g, ''))}
                                />
                            </div>
                        </label>
                        <PermissionWrapper permission={roles.process.permissions.read.key}>
                            <UiButton type="submit" className="h-10 gap-2" disabled={loadingPreparation || !budgetCodeInput}>
                                <Search className="h-4 w-4" strokeWidth={1.5} />
                                {loadingPreparation ? 'Buscando…' : 'Buscar orçamento'}
                            </UiButton>
                        </PermissionWrapper>
                    </form>

                    {(searchError || preparationError) && (
                        <p className="mt-3 rounded-r-md border-l-[3px] border-l-brand-pink bg-od-secondary-50 px-3 py-2 text-sm text-od-secondary-600" role="alert">
                            {searchError ?? preparationError}
                        </p>
                    )}
                    {loadingPreparation && <Loader />}

                    {context && (
                        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 rounded-md bg-secondary px-4 py-3 text-sm md:grid-cols-4" data-testid="budget-summary">
                            <div><dt className={fieldLabel}>Orçamento</dt><dd className="font-semibold">#{context.budget.code ?? searchedCode}{context.budget.status ? ` · ${context.budget.status}` : ''}</dd></div>
                            <div><dt className={fieldLabel}>Cliente</dt><dd className="truncate">{context.budget.customer?.name ?? '-'}</dd></div>
                            <div><dt className={fieldLabel}>Empresa</dt><dd className="truncate">{context.budget.company?.name ?? '-'}</dd></div>
                            <div><dt className={fieldLabel}>Itens</dt><dd>{itemCount} {itemCount === 1 ? 'item' : 'itens'} · {totalQuantity} un.</dd></div>
                        </dl>
                    )}
                </Step>

                {preparation && (
                    <Step n={2} title="Como organizar a compra?">
                        <div className="mb-4 inline-flex flex-wrap rounded-full bg-secondary p-1" role="tablist" aria-label="Modo de criação">
                            {([
                                ['split', 'Separar em processos', Layers, 'Um processo por produto ou grupo de itens'],
                                ['single', 'Um processo único', FileText, 'Todos os itens numa só compra'],
                            ] as const).map(([value, label, Icon, hint]) => (
                                <button
                                    key={value}
                                    type="button"
                                    role="tab"
                                    title={hint}
                                    aria-selected={mode === value}
                                    onClick={() => setMode(value)}
                                    className={cn(
                                        'inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
                                        mode === value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-accent hover:text-brand-deep'
                                    )}
                                >
                                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                                    {label}
                                </button>
                            ))}
                        </div>

                        {mode === 'split' ? (
                            <BudgetItemOrganizer preparation={preparation} creating={creatingBatch} onConfirm={handleConfirmOrganizer} />
                        ) : (
                            <form onSubmit={onSubmitSingle} className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px]" data-testid="single-process-form">
                                <label className="block">
                                    <span className={fieldLabel}>Título do processo</span>
                                    <Input aria-label="Título do processo" {...register('title')} />
                                    {errors.title && <span className="mt-1 block text-xs text-od-secondary-600">{errors.title.message}</span>}
                                </label>
                                <label className="block">
                                    <span className={fieldLabel}>Quantidade</span>
                                    <Input aria-label="Quantidade" type="number" min={1} {...register('quantity', { valueAsNumber: true })} />
                                    {errors.quantity && <span className="mt-1 block text-xs text-od-secondary-600">{errors.quantity.message}</span>}
                                </label>
                                <div className="md:col-span-2" data-testid="single-process-items">
                                    <span className={fieldLabel}>Itens do orçamento neste processo</span>
                                    {freeItems.length ? (
                                        <ul className="space-y-0.5 text-sm">
                                            {freeItems.map((item) => (
                                                <li key={item.externalId}>{item.quantity ?? '-'} × {item.name ?? item.sku ?? 'item'}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-od-secondary-600">Todos os itens deste orçamento já estão vinculados a outros processos.</p>
                                    )}
                                </div>
                                <label className="block md:col-span-2">
                                    <span className={fieldLabel}>Observações (opcional)</span>
                                    <Textarea aria-label="Observações" rows={2} {...register('notes')} />
                                </label>
                                <div className="flex flex-wrap gap-2 md:col-span-2">
                                    <UiButton type="submit" disabled={creatingBatch || freeItems.length === 0}>{creatingBatch ? 'Criando…' : 'Criar processo'}</UiButton>
                                    <UiButton type="button" variant="ghost" onClick={() => navigate(RoutesPaths.PurchasingList)}>Cancelar</UiButton>
                                </div>
                            </form>
                        )}
                    </Step>
                )}
            </div>
            )}
        </Container>
    )
}

// Keep PurchaseProcess import referenced for typing of navigation results
export type { PurchaseProcess }
